/* Los topes, sus nombres y las reglas de grandfathering viven en
   `@webbuilder/plans`: es lo mismo que aplica el servidor. Se reexportan desde
   aquí porque medio panel ya los importa de este módulo. */
import { NEXT_PLAN, PLAN_LABEL, formatPlanLimit as formatPlanLimitIn } from '@webbuilder/plans'
import { activeLanguage } from './i18n'
import type { Translate } from '@webbuilder/i18n'
import type { LimitedResource as LimitedResourceType } from '@webbuilder/plans'
import type { BillingPlan } from './api'

export { NEXT_PLAN, PLAN_LABEL }

/**
 * Un tope, en el idioma del PANEL.
 *
 * Se envuelve aquí, en el único sitio por el que pasa todo el panel, en vez de
 * pasarle el idioma a cada una de las diez llamadas: así ninguna se queda atrás
 * el día que se añada la once. El de `@webbuilder/plans` recibe el idioma
 * porque el worker, que lo comparte, pinta en el del proyecto.
 */
export function formatPlanLimit(limit: number | null): string {
  return formatPlanLimitIn(limit, activeLanguage())
}

/**
 * «12 páginas», «1 colección», para armar frases.
 *
 * Tiene su versión aquí y no se reexporta la de `@webbuilder/plans` porque
 * aquélla lleva los nombres escritos en español —la usan los errores del
 * worker, que es otro problema— y ésta los saca del catálogo. El mapa guarda
 * CLAVES: una clave compuesta al vuelo no la puede comprobar nadie, ni el
 * compilador ni la regla que vigila que existan.
 */
const RESOURCE_NOUN_KEY: Record<LimitedResourceType, { one: string; many: string }> = {
  pages: { one: 'plans.noun.pages.one', many: 'plans.noun.pages.many' },
  collections: { one: 'plans.noun.collections.one', many: 'plans.noun.collections.many' },
  cmsItems: { one: 'plans.noun.cmsItems.one', many: 'plans.noun.cmsItems.many' },
  contacts: { one: 'plans.noun.contacts.one', many: 'plans.noun.contacts.many' },
  teamMembers: { one: 'plans.noun.teamMembers.one', many: 'plans.noun.teamMembers.many' },
  offers: { one: 'plans.noun.offers.one', many: 'plans.noun.offers.many' },
  blogPosts: { one: 'plans.noun.blogPosts.one', many: 'plans.noun.blogPosts.many' },
}

/**
 * Lo que no cabe en el plan, escrito: «12 páginas (tu plan permite 3)».
 *
 * Vive aquí y no en la pantalla del traspaso porque lo dicen dos: el asistente
 * de entrega y el aviso del inicio del proyecto. Escrito dos veces acabarían
 * contando la misma cifra con otras palabras.
 */
export function shortfallList(
  shortfalls: { resource: LimitedResourceType; value: number; cap: number }[],
  t: Translate,
): string {
  return shortfalls
    .map((item) => t('handover.shortfall', {
      count: countLabel(item.resource, item.value, t),
      cap: formatPlanLimit(item.cap),
    }))
    .join('; ')
}

export function countLabel(resource: LimitedResourceType, count: number, t: Translate): string {
  const noun = RESOURCE_NOUN_KEY[resource]
  return t(count === 1 ? noun.one : noun.many, { count: formatPlanLimit(count) })
}

export {
  PLAN_LIMITS as SITE_PLAN_LIMITS,
  /* El cupo de proyectos NO es un tope de sitio: se cuenta contra la membresía
     entera, así que va con su nombre y no bajo el alias de arriba. */
  PROJECT_LIMITS,
  ADMIN_PROJECT_LIMIT,
  ELITE_PROJECT_LIMIT,
  PLATFORM_FEE_PERCENT,
  canConnectStripe,
  reachedLimit,
  overageOf,
  overagesFor,
  type PlanLimits as SitePlanLimits,
  type LimitedResource,
  type Overage,
} from '@webbuilder/plans'

export type UsageState = 'ok' | 'warn' | 'full'

/** Fraction used (0–1+). Unlimited (`null`) is always 0. */
export function usageRatio(used: number, limit: number | null): number {
  if (limit === null || limit <= 0) return 0
  return used / limit
}

/** ok < 80% ≤ warn < 100% ≤ full. Unlimited limits are always ok. */
export function usageState(used: number, limit: number | null): UsageState {
  if (limit === null) return 'ok'
  const ratio = usageRatio(used, limit)
  if (ratio >= 1) return 'full'
  if (ratio >= 0.8) return 'warn'
  return 'ok'
}

/** Every counter the dashboard puts a limit on, in widget and usage screens. */
export type UsageResource =
  | 'pages' | 'collections' | 'cmsItems' | 'contacts' | 'offers' | 'teamMembers' | 'blogPosts'
  | 'ai' | 'forms' | 'storage' | 'bandwidth'
  /* De la cuenta, no de un sitio: ver «Uso de la cuenta». */
  | 'projects' | 'generations'

/**
 * What actually happens when a counter fills up.
 *
 * `effect` is written to complete "Al llegar al 100%, …", so it must stay in
 * the future tense and describe the real behaviour — not a generic scare. Pages,
 * collections, CMS items, storage and AI are blocked for real; form submissions
 * keep arriving flagged as over-limit; contacts are not enforced anywhere, so
 * their copy promises no blocking that never comes. Offers block PUBLISHING and
 * nothing else: escribirlas y guardarlas sigue siendo gratis, y su texto lo
 * dice — prometer que «no podrás crear más» sería falso y encima desanimaría lo
 * único que sí se quiere que pase en el plan gratis.
 *
 * `bandwidth` se queda en el registro por el tipo, pero ya no puede saltar: el
 * tráfico dejó de tener tope —se sirve con la salida gratuita de R2 y se acota
 * con la cláusula de uso razonable, no con un número— y la pantalla de uso le
 * pasa `limit: null`, con lo que `usageState` siempre devuelve «ok».
 */
/* Claves, no texto: es una constante de módulo y ahí no se puede llamar a un
   hook. `usageAlertMessage` las resuelve con el traductor que le pasan. */
const USAGE_COPY: Record<UsageResource, { noun: string; effect: string }> = {
  pages: { noun: 'usage.noun.pages', effect: 'usage.effect.pages' },
  collections: { noun: 'usage.noun.collections', effect: 'usage.effect.collections' },
  cmsItems: { noun: 'usage.noun.cmsItems', effect: 'usage.effect.cmsItems' },
  contacts: { noun: 'usage.noun.contacts', effect: 'usage.effect.contacts' },
  offers: { noun: 'usage.noun.offers', effect: 'usage.effect.offers' },
  teamMembers: { noun: 'usage.noun.teamMembers', effect: 'usage.effect.teamMembers' },
  blogPosts: { noun: 'usage.noun.blogPosts', effect: 'usage.effect.blogPosts' },
  ai: { noun: 'usage.noun.ai', effect: 'usage.effect.ai' },
  forms: { noun: 'usage.noun.forms', effect: 'usage.effect.forms' },
  storage: { noun: 'usage.noun.storage', effect: 'usage.effect.storage' },
  bandwidth: { noun: 'usage.noun.bandwidth', effect: 'usage.effect.bandwidth' },
  projects: { noun: 'usage.noun.projects', effect: 'usage.effect.projects' },
  generations: { noun: 'usage.noun.generations', effect: 'usage.effect.generations' },
}

/** The hover explanation behind a warning badge: what happens, not just that
 *  something is happening. `null` while the counter is healthy. */
export function usageAlertMessage(
  resource: UsageResource,
  state: UsageState,
  plan: BillingPlan,
  ratio: number,
  t: Translate,
): string | null {
  if (state === 'ok') return null
  const copy = USAGE_COPY[resource]
  const noun = t(copy.noun)
  const effect = t(copy.effect)
  const next = NEXT_PLAN[plan]
  const upsell = next
    ? t('usage.alert.upgrade', { plan: PLAN_LABEL[next] })
    : t('usage.alert.contact')
  if (state === 'warn') {
    return t('usage.alert.warn', { percent: Math.round(ratio * 100), noun, plan: PLAN_LABEL[plan], effect })
  }
  return t('usage.alert.full', { noun, plan: PLAN_LABEL[plan], effect, upsell })
}
